import { t as localeText } from "@/shared/lib/translate";
import { useTranslation } from "react-i18next";
import { useShortcutsHelp } from "@/shared/lib/hooks/useShortcutsHelp";
import { formatChord, formatShortcut } from "@/shared/lib/shortcut";
import { Dialog } from "./Dialog";
import { Kbd, KbdGroup } from "./Kbd";
import { Text } from "./Text";

/** A single keyboard shortcut shown in the help overlay. Either a combo
 *  (`shortcut`, e.g. `"mod+k"`) or a leader chord (`chord`, e.g. `"g i"`). */
export interface ShortcutHint {
  /** Combo string, e.g. `"mod+shift+o"` — rendered via `formatShortcut`. */
  shortcut?: string;
  /** Leader chord, e.g. `"g i"` (press in sequence) — rendered via `formatChord`. */
  chord?: string;
  /** Already-translated description of what the shortcut does. */
  label: string;
}

export interface ShortcutsDialogProps {
  /** Shortcuts to list, in display order. */
  hints: ShortcutHint[];
}

/**
 * The keyboard-shortcuts help sheet (portal + ink scrim + paper card). Lists
 * every global shortcut with its keycaps, rendered through `formatShortcut` so
 * the combos match the user's platform (⌘ vs Ctrl). Open state lives in a tiny
 * module store (`useShortcutsHelp`) so any control or shortcut can summon it.
 * Mount once in the app shell.
 */
export function ShortcutsDialog({ hints }: ShortcutsDialogProps) {
  const { t } = useTranslation();
  const { open, setOpen } = useShortcutsHelp();

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      title={t("shortcuts.title")}
      closeLabel={t("shortcuts.close")}
      className="max-w-[400px] [&>header]:items-center [&>header]:py-3"
      contentClassName="pt-0 pb-4"
    >
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {hints.map((hint) => (
            <li
              key={hint.shortcut ?? hint.chord}
              className="flex min-h-8 items-center justify-between gap-4"
            >
              <Text variant="body" as="span" className="min-w-0 flex-1 truncate text-ink-soft">
                {hint.label}
              </Text>
              <KbdGroup>
                {(hint.chord ? formatChord(hint.chord) : formatShortcut(hint.shortcut ?? "")).map(
                  (token, i) => (
                    <Kbd key={i}>{token}</Kbd>
                  ),
                )}
              </KbdGroup>
            </li>
          ))}
        </ul>
        <p className="m-0 mt-3 flex items-center gap-1.5 text-[11px] text-ink-muted">
          <Kbd>{localeText("interface.esc_abb761d9", { defaultValue: "esc" })}</Kbd>
          <span>{t("shortcuts.close")}</span>
        </p>
    </Dialog>
  );
}
